import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/index.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import PostCard from '../components/PostCard.jsx';
import { useLocale } from '../contexts/LocaleContext.jsx';

export default function Feed() {
  const { user } = useAuth();
  const { t } = useLocale();
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const loaderRef = useRef(null);

  const loadPage = useCallback(async (p) => {
    if (loading) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.posts.feed(p);
      const newPosts = data.posts ?? [];
      setPosts((prev) => (p === 1 ? newPosts : [...prev, ...newPosts]));
      setHasMore(data.has_more ?? newPosts.length === 12);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Initial load
  useEffect(() => { loadPage(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Infinite scroll observer
  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((p) => {
            const next = p + 1;
            loadPage(next);
            return next;
          });
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loading, loadPage]);

  function handleDelete(deletedId) {
    if (!window.confirm('Delete this post?')) return;
    api.posts.delete(deletedId).then(() => {
      setPosts((prev) => prev.filter((p) => p.id !== deletedId));
    }).catch(console.error);
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-gray-800">{t('feed')}</h1>
        {user?.role === 'caddie' && (
          <Link to="/upload" className="btn-primary text-sm">
            + {t('newPost')}
          </Link>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">
          {error}
        </div>
      )}

      {/* Grid */}
      {posts.length === 0 && !loading ? (
        <div className="text-center py-20 text-gray-400">
          <div className="text-5xl mb-3">📸</div>
          <p className="font-medium">{t('noFeedPosts')}</p>
          {user?.role === 'caddie' && (
            <Link to="/upload" className="btn-primary mt-4 inline-flex">{t('firstPost')}</Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      <div ref={loaderRef} className="h-10 flex items-center justify-center mt-4">
        {loading && (
          <div className="w-6 h-6 border-3 border-brand-600 border-t-transparent rounded-full animate-spin" />
        )}
        {!hasMore && posts.length > 0 && (
          <p className="text-sm text-gray-400">You've seen it all ⛳</p>
        )}
      </div>
    </div>
  );
}
